import styles from './BillingPage.module.css';
import React, { useEffect, useState } from 'react';
import ContractsCard from '../components/ContractsCard';
import { useNavigate } from 'react-router-dom';
import { useBilling } from '../../../context/BillingContext';
import { useAuth } from '../../../context/AuthContext';
import { useTheme } from '../../../context/ThemeContext';
import { CheckCircle2, CreditCard, Wallet } from 'lucide-react';
import {
  cancelSubscription,
  fetchPlans,
  Plan,
  sendContactMessage,
  fmtQuantity,
} from '../../../api/billingApi';
import UsageMeters, { fmtLimit, useUsage } from '../components/UsageMeters';
import CreditsPanel from '../components/CreditsPanel';
import { fetchInvoices, type Invoice } from '../../../api/billingApi';
/** Stripe sends the customer back here with ?credits=success or ?credits=cancelled. */
function creditsReturnFromUrl(): 'success' | 'cancelled' | null {
  const v = new URLSearchParams(window.location.search).get('credits');
  return v === 'success' || v === 'cancelled' ? v : null;
}

// Feature labels for display (same as PricingPage)
const FEATURE_LABELS: { key: keyof Plan['features']; label: string }[] = [
  { key: 'user_seats', label: 'User seats' },
  { key: 'jurisdictions', label: 'Jurisdictions' },
  { key: 'ai_summaries_per_month', label: 'AI queries/mo' },
  { key: 'documents_per_month', label: 'Document analyses/mo' },
  { key: 'multi_jurisdiction', label: 'Multi-jurisdiction tracking' },
  { key: 'custom_alerts', label: 'Custom alerts' },
  { key: 'export_reporting', label: 'Export & reporting' },
  { key: 'advanced_ai', label: 'Advanced AI analysis' },
  { key: 'api_access', label: 'API access' },
  { key: 'team_collaboration', label: 'Team collaboration' },
  { key: 'sso', label: 'SSO / SCIM' },
  { key: 'predictive_analytics', label: 'Early Warning System' },
  { key: 'dedicated_support', label: 'Dedicated CSM' },
];

const TIER_DISPLAY: Record<string, string> = {
  academic: 'Academic',
  startup: 'Startup',
  professional: 'Professional',
  business: 'Business',
  growth: 'Growth',
  law_firm_individual: 'Law Firm — Individual',
  law_firm_team: 'Law Firm — Team',
  law_firm_firm: 'Law Firm — Firm-wide',
  government_state: 'Government — State/Local',
  government_federal: 'Government — Federal',
  enterprise: 'Enterprise',
};

const STATUS_BADGE: Record<string, { label: string; color: string }> = {
  trialing:  { label: 'Free Trial',  color: 'var(--ds-text)' },
  active:    { label: 'Active',      color: '#039855' },
  cancelled: { label: 'Cancelled',   color: '#b54708' },
  expired:   { label: 'Expired',     color: '#d92d20' },
  past_due:  { label: 'Past Due',    color: '#d92d20' },
};

const BillingPage: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { theme } = useTheme();
  const { subscription, loading, trialDaysRemaining, refresh, loadFailed } = useBilling();
  const usage = useUsage();
  // SRS #83: the hero's "charged" figure comes from the latest completed payment, not a hard-coded ₹0
  const [lastPayment, setLastPayment] = useState<Invoice | null>(null);
  useEffect(() => { fetchInvoices().then(rows => setLastPayment(rows.find(r => r.status === 'completed') ?? null)).catch(() => setLastPayment(null)); }, []);
  const money = (r: Invoice) => `${new Intl.NumberFormat(undefined, { style: 'currency', currency: r.currency || 'USD', maximumFractionDigits: 0 }).format(Number(r.amount))}`;
  const aiMeter = usage?.meters.find(m => m.meter === 'ai_queries');
  const [plans, setPlans] = useState<Plan[]>([]);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactSubject, setContactSubject] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [contactSending, setContactSending] = useState(false);
  const [contactSent, setContactSent] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchPlans()
      .then(setPlans)
      .catch(() => setPlans([]));
  }, []);

  // No subscription: the page says so and offers the plans (see the card below). It used to
  // redirect silently to /pricing, which read as a broken page to an account with no plan.

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await cancelSubscription(false); // cancel at period end
      await refresh();
      setCancelConfirm(false);
    } catch {/* */} finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingPage}>
        <div className={styles.spinner} />
      </div>
    );
  }

  const sub = subscription;
  const statusInfo = sub ? STATUS_BADGE[sub.status] : null;

  /** "1 Oct 2026" — the long form used across the hero. */
  const longDate = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

  /** Days left in whichever period the account is actually in: the trial while
   *  trialling, the billing period once paid. Reading only the trial left a
   *  paying customer looking at a dash. */
  const daysRemaining: number | null = (() => {
    if (!sub) return null;
    if (sub.status === 'trialing') return trialDaysRemaining;
    if (!sub.current_period_end) return null;
    const ms = new Date(sub.current_period_end).getTime() - Date.now();
    return Number.isNaN(ms) ? null : Math.max(0, Math.ceil(ms / 86_400_000));
  })();
  // trialPct intentionally removed — the new dashboard computes its own
  // percentage inside the trial card based on actual trial start/end dates.

  const isEnterprise = sub?.plan_tier === 'enterprise';
  // The page always sits inside a shell now — /billing moved under AppLayout
  // when the sidebar stopped being suppressed there — so its own POLICYORA bar
  // and Log out button would be a second set of the same chrome.
  const isEmbeddedDashboardBilling = true;

  if (isEnterprise) {
    const planObj = plans.find(p => p.tier === sub!.plan_tier);
    const statusInfo = sub ? STATUS_BADGE[sub.status] : null;
    return (
      <div className={styles.enterprisePage}>
        {!isEmbeddedDashboardBilling && (
          <div className={styles.enterpriseHeader}>
            <button className={styles.enterpriseHeaderLogout} onClick={logout}>Sign out</button>
          </div>
        )}

        <div className={styles.enterpriseBody}>
          {/* Plan Hero */}
          <div className={styles.enterprisePlanHero}>
            <div className={styles.enterprisePlanLeft}>
              <div className={styles.enterprisePlanBadge}>Enterprise Plan</div>
              <div className={styles.enterprisePlanTier}>{TIER_DISPLAY[sub!.plan_tier] ?? sub!.plan_tier}</div>
              {sub!.billing_cycle && (
                <div className={styles.enterprisePlanMeta}>
                  {sub!.billing_cycle === 'annual' ? 'Annual billing' : 'Monthly billing'}
                  {sub!.amount && ` · $${parseFloat(sub!.amount).toLocaleString()}/${sub!.billing_cycle === 'annual' ? 'yr' : 'mo'}`}
                </div>
              )}
              <div className={styles.enterprisePlanStatus}>
                <span className={styles.enterprisePlanStatusDot} />
                {statusInfo?.label ?? sub!.status}
              </div>
            </div>
            <div className={styles.enterprisePlanRight}>
              <button className={styles.enterpriseChangePlanBtn} onClick={() => navigate('/pricing', { state: { changeOnly: true } })}>
                Change Plan
              </button>
            </div>
          </div>

          <div className={styles.enterpriseGrid}>
            {/* Actions */}
            <div className={styles.enterpriseCard}>
              <div className={styles.enterpriseCardTitle}>Actions</div>
              <div className={styles.enterpriseActionGrid}>
                <button className={styles.enterpriseActionBtn} onClick={() => navigate('/enterprise/dashboard')}>
                  <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"/></svg>
                  Dashboard
                </button>
                <button className={styles.enterpriseActionBtn} onClick={() => navigate('/billing/payment-history')}>
                  <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z"/><path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd"/></svg>
                  Payment History
                </button>
                <button className={styles.enterpriseActionBtn} onClick={() => { setContactOpen(true); setContactSent(false); setContactError(null); }}>
                  <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/><path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/></svg>
                  Contact Team
                </button>
                <button className={styles.enterpriseActionBtn} onClick={() => navigate('/pricing')}>
                  <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/><path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd"/></svg>
                  View All Plans
                </button>
                {(sub!.status as string) === 'active' && !sub!.cancel_at_period_end && (
                  <button className={`${styles.enterpriseActionBtn} ${styles.enterpriseActionBtnDanger}`} onClick={() => setCancelConfirm(true)}>
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
                    Cancel Subscription
                  </button>
                )}
                {sub!.cancel_at_period_end && (
                  <div style={{ gridColumn: 'span 2', fontSize: 12, color: '#b54708', background: '#fffaeb', border: '1px solid #fde68a', borderRadius: 7, padding: '8px 12px' }}>
                    Cancels at end of billing period
                  </div>
                )}
              </div>
            </div>

            {/* Plan Features */}
            <div className={styles.enterpriseCard}>
              <div className={styles.enterpriseCardTitle}>Included Features</div>
              {planObj ? (
                <ul className={styles.enterpriseFeatureList}>
                  {FEATURE_LABELS.map(({ key, label }) => (
                    <li key={key} className={styles.enterpriseFeatureItem}>
                      <span className={styles.enterpriseFeatureCheck}>✓</span>
                      <span>{typeof planObj.features[key] !== 'boolean' ? `${fmtQuantity(key, planObj.features[key])} ${label.toLowerCase()}` : label}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div style={{ color: 'var(--ds-text-muted)', fontSize: 13 }}>Features not available.</div>
              )}
            </div>
          </div>

          {sub!.current_period_end && sub!.status === 'active' && (
            <div style={{ textAlign: 'center', color: '#667085', fontSize: 13, marginTop: 8 }}>
              {sub!.cancel_at_period_end ? 'Ends' : sub!.auto_renews ? 'Renews automatically on' : 'Expires'} {new Date(sub!.current_period_end).toLocaleDateString()}
              {!sub!.auto_renews && !sub!.cancel_at_period_end && (
                <button type="button" onClick={() => navigate(`/billing/checkout?plan=${sub!.plan_tier}&cycle=${sub!.billing_cycle || 'monthly'}`)}
                        style={{ marginLeft: 10, background: 'none', border: '1px solid #444ce7', color: '#444ce7', borderRadius: 6, padding: '3px 10px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                  Renew now
                </button>
              )}
            </div>
          )}
          <ContractsCard />
          <CreditsPanel returned={creditsReturnFromUrl()} />
          {actionError && (
            <div style={{ marginTop: 12, background: '#fef3f2', color: 'var(--ds-danger-text)', border: '1px solid #f97066', borderRadius: 8, padding: '10px 20px', textAlign: 'center', fontSize: 14 }}>
              {actionError}
              <button style={{ marginLeft: 12, background: 'none', border: 'none', color: 'var(--ds-danger-text)', cursor: 'pointer', fontWeight: 700 }} onClick={() => setActionError(null)}>✕</button>
            </div>
          )}
        </div>

        {/* Contact modal */}
        {contactOpen && (
          <div className={styles.dialogOverlay} onClick={() => setContactOpen(false)}>
            <div className={styles.dialog} onClick={e => e.stopPropagation()} style={{ maxWidth: 460, width: '100%' }}>
              <h3 style={{ margin: '0 0 6px 0', fontSize: 18, fontWeight: 800 }}>Contact Our Team</h3>
              <p style={{ margin: '0 0 18px 0', color: '#667085', fontSize: 14 }}>Send us a message and we'll get back to you shortly.</p>
              {contactSent ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ marginBottom: 10 }}><CheckCircle2 size={40} aria-hidden /></div>
                  <div style={{ fontWeight: 700, fontSize: 16 }}>Message sent!</div>
                  <div style={{ color: '#667085', fontSize: 13, marginTop: 4 }}>We'll respond within 1–2 business days.</div>
                  <button className={styles.upgradeBtn} style={{ marginTop: 18 }} onClick={() => setContactOpen(false)}>Close</button>
                </div>
              ) : (
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  setContactSending(true); setContactError(null);
                  try {
                    await sendContactMessage({ subject: contactSubject, message: contactMessage });
                    setContactSent(true); setContactSubject(''); setContactMessage('');
                  } catch { setContactError('Failed to send. Please try again.'); } finally { setContactSending(false); }
                }}>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Subject</label>
                    <input type="text" required value={contactSubject} onChange={e => setContactSubject(e.target.value)} placeholder="e.g. Issue with my plan" style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid #eaecf0', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Message</label>
                    <textarea required value={contactMessage} onChange={e => setContactMessage(e.target.value)} placeholder="Describe your issue or question..." rows={5} style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid #eaecf0', fontSize: 14, outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
                  </div>
                  {contactError && <div style={{ color: '#d92d20', fontSize: 13, marginBottom: 10 }}>{contactError}</div>}
                  <div className={styles.dialogActions}>
                    <button type="button" className={styles.dialogCancel} onClick={() => setContactOpen(false)}>Cancel</button>
                    <button type="submit" className={styles.upgradeBtn} disabled={contactSending}>{contactSending ? 'Sending…' : 'Send Message'}</button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}

        {/* Cancel confirm */}
        {cancelConfirm && (
          <div className={styles.dialogOverlay}>
            <div className={styles.dialog}>
              <h3>Cancel subscription?</h3>
              <p>You'll keep access until the end of your current billing period.</p>
              <div className={styles.dialogActions}>
                <button className={styles.dialogCancel} onClick={handleCancel} disabled={cancelling}>{cancelling ? 'Cancelling…' : 'Yes, cancel'}</button>
                <button className={styles.dialogKeep} onClick={() => setCancelConfirm(false)}>Keep subscription</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`${styles.billingPage} ${theme === 'dark' ? styles.billingPageDark : ''}`}>
      {!isEmbeddedDashboardBilling && (
        <div className={styles.heroHeader}>
          <h1 className={styles.heroTitle}>POLICYORA</h1>
          <div className={styles.heroActionsRight}>
            <button type="button" className={styles.logoutBtn} onClick={logout}>
              Log out
            </button>
          </div>
        </div>
      )}

      {/* UI-friendly error message */}
      {actionError && (
        <div className={styles.errorBanner}>
          {actionError}
          <button className={styles.errorBannerDismiss} onClick={() => setActionError(null)}>Dismiss</button>
        </div>
      )}

      {!sub ? (
        <section className={styles.noPlanCard} aria-label="No active plan">
          {loadFailed ? (
            <>
              <h2 className={styles.noPlanTitle}>Your billing details could not be loaded</h2>
              <p className={styles.noPlanText}>Refresh the page. If it still fails, contact support.</p>
            </>
          ) : (
            <>
              <h2 className={styles.noPlanTitle}>Your account has no active plan</h2>
              <p className={styles.noPlanText}>
                Choose a plan to start using PolicyOra. Once a plan is active, you can manage it, see invoices and buy AI credits here.
              </p>
              <button type="button" className={styles.noPlanButton} onClick={() => navigate('/pricing')}>
                View plans
              </button>
            </>
          )}
        </section>
      ) : (
        <section className={styles.dashboardWrap}>
          {/* ===================== HERO CARD ===================== */}
          <div className={styles.heroCard}>
            <div className={styles.heroLeft}>
              <span className={styles.heroEyebrow}>
                <span className={styles.heroEyebrowDot} aria-hidden="true" />
                CURRENT PLAN
              </span>
              <h2 className={styles.heroTitleNew}>
                {sub.status === 'trialing' && trialDaysRemaining !== null ? (
                  <>Congrats! You're on your <span className={styles.heroAccent}>free trial</span></>
                ) : sub.status === 'trialing' ? (
                  <>Your <span className={styles.heroAccentRed}>free trial</span> has ended</>
                ) : (
                  <>You're subscribed to <span className={styles.heroAccent}>{TIER_DISPLAY[sub.plan_tier] ?? sub.plan_tier}</span></>
                )}
              </h2>
              {/* The trial dates belong to a trial. They were shown on a paid
                  subscription too, so an account that had trialled before
                  buying read "Started 1 Oct · Ends 15 Oct" from the old trial
                  while the card below it said the subscription expires a year
                  later — two dates on one screen, disagreeing. A paid plan
                  shows its own billing period. */}
              <div className={styles.heroSubtitle} data-testid="hero-subtitle">
                {TIER_DISPLAY[sub.plan_tier] ?? sub.plan_tier} plan
                {sub.status === 'trialing' ? (
                  <>
                    {sub.trial_started_at && ` · Started ${longDate(sub.trial_started_at)}`}
                    {sub.trial_ends_at && ` · Trial ends ${longDate(sub.trial_ends_at)}`}
                  </>
                ) : (
                  <>
                    {sub.current_period_start && ` · Started ${longDate(sub.current_period_start)}`}
                    {sub.current_period_end &&
                      ` · ${sub.cancel_at_period_end ? 'Ends' : sub.auto_renews ? 'Renews' : 'Expires'} ${longDate(sub.current_period_end)}`}
                  </>
                )}
              </div>
            </div>
            <div className={styles.heroStats}>
              <div className={styles.heroStat}>
                <div className={styles.heroStatLabel}>DAYS LEFT</div>
                {/* Read only the trial before, so a paying customer always saw
                    a dash where the number should be. A paid plan counts down
                    its billing period. */}
                <div className={`${styles.heroStatValue} ${styles.heroStatValueAccent}`} data-testid="hero-days-left">
                  {daysRemaining ?? '—'}
                </div>
              </div>
              <div className={styles.heroStat}>
                <div className={styles.heroStatLabel}>LAST CHARGE</div>
                <div className={styles.heroStatValue} data-testid="hero-last-charge" title={lastPayment?.created_at ? new Date(lastPayment.created_at).toLocaleDateString() : undefined}>{lastPayment ? money(lastPayment) : '—'}</div>
              </div>
              <div className={styles.heroStat}>
                <div className={styles.heroStatLabel}>AI QUERIES</div>
                <div className={styles.heroStatValue} data-testid="hero-ai-queries">{aiMeter ? fmtLimit(aiMeter) : '—'}</div>
              </div>
            </div>
          </div>

          <UsageMeters usage={usage} />

          <CreditsPanel returned={creditsReturnFromUrl()} />

          {/* ===================== 3-COLUMN GRID ===================== */}
          <div className={styles.dashboardGrid}>

            {/* LEFT — ACTIONS */}
            <aside className={styles.actionsCol}>
              <div className={styles.actionsLabel}>ACTIONS</div>
              <button
                className={styles.actionPrimary}
                onClick={() => navigate('/pricing')}
              >
                {sub.status === 'trialing' ? 'Upgrade Plan' : 'Change Plan'}
              </button>
              <button className={styles.actionSecondary} onClick={() => navigate('/pricing')}>
                View All Plans
              </button>
              <button className={styles.actionSecondary} onClick={() => { setContactOpen(true); setContactSent(false); setContactError(null); }}>
                Contact Team
              </button>
              <button className={styles.actionSecondary} onClick={() => navigate('/billing/payment-history')}>
                Payment History
              </button>
              <button
                className={styles.actionSecondary}
                onClick={() => {
                  if (sub.status === 'trialing' && trialDaysRemaining === null) { setActionError('Your free trial has ended. Please upgrade to continue.'); return; }
                  navigate('/app');
                }}
              >
                Go to Chat
              </button>
              <button
                className={styles.actionAccent}
                onClick={() => {
                  if (sub.status === 'trialing' && trialDaysRemaining === null) { setActionError('Your free trial has ended. Please upgrade to continue.'); return; }
                  navigate('/dashboard');
                }}
              >
                Go to Dashboard
              </button>
              {(sub.status as string) === 'active' && !sub.cancel_at_period_end && (
                <button className={styles.actionDanger} onClick={() => setCancelConfirm(true)}>
                  Cancel Subscription
                </button>
              )}
            </aside>

            {/* CENTER — CURRENT PLAN CARD */}
            <div className={styles.planCol}>
              {/* Dark top with dot-matrix + plan name + "YOUR PLAN" badge */}
              <div className={styles.planTop}>
                <span className={styles.planTag}>YOUR PLAN</span>
                <div className={styles.planHeader}>
                  <div className={styles.planName}>{TIER_DISPLAY[sub.plan_tier] ?? sub.plan_tier}</div>
                  {sub.plan_tier === 'academic' && (
                    <div className={styles.planTagline}>Free for verified .edu · SheerID</div>
                  )}
                </div>
              </div>

              {/* Light body with status pill + features + change-plan button */}
              <div className={styles.planBody}>

                {sub.status === 'trialing' && trialDaysRemaining !== null && (
                  <div className={styles.planStatusPill}>
                    <span className={styles.planStatusDot} aria-hidden="true" />
                    Free Trial Active
                  </div>
                )}
                {sub.status === 'active' && (
                  <div className={`${styles.planStatusPill} ${styles.planStatusPillActive}`}>
                    <span className={styles.planStatusDot} aria-hidden="true" />
                    {statusInfo?.label ?? 'Active'}
                  </div>
                )}

                {sub.billing_cycle && (
                  <div className={styles.planMetaLine}>
                    {sub.billing_cycle === 'annual' ? 'Annual billing' : 'Monthly billing'}
                    {sub.amount && ` · $${parseFloat(sub.amount).toLocaleString()}/${sub.billing_cycle === 'annual' ? 'yr' : 'mo'}`}
                  </div>
                )}
                {sub.cancel_at_period_end && (
                  <div className={styles.cancelNotice}>Cancels at end of billing period</div>
                )}

                {plans.length > 0 && (
                  <ul className={styles.planFeaturesList}>
                    {(() => {
                      const planObj = plans.find(p => p.tier === sub.plan_tier);
                      if (!planObj) return <li className={styles.planFeaturesEmpty}>Features not available.</li>;
                      return FEATURE_LABELS.map(({ key, label }) => {
                        const v = planObj.features[key];
                        const hasIt = typeof v === 'boolean' ? v : true;
                        return (
                          <li key={key} className={styles.planFeatureItem}>
                            <span className={`${styles.planFeatureCheck} ${hasIt ? '' : styles.planFeatureCheckMuted}`} aria-hidden="true">
                              {hasIt ? '✓' : '—'}
                            </span>
                            <span className={hasIt ? '' : styles.planFeatureMuted}>
                              {typeof v !== 'boolean' ? `${fmtQuantity(key, v)} ${label.toLowerCase()}` : label}
                            </span>
                          </li>
                        );
                    });
                  })()}
                </ul>
              )}

                <button
                  className={styles.changePlanCta}
                  onClick={() => navigate('/pricing', { state: { changeOnly: true } })}
                >
                  Change Plan
                </button>
              </div> {/* /.planBody */}
            </div>

            {/* RIGHT — TRIAL IN PROGRESS + TIMELINE */}
            <div className={styles.trialCol}>
              {sub.status === 'trialing' && trialDaysRemaining !== null ? (() => {
                /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
                const daysLeft = trialDaysRemaining!;   // narrowed by the && above; IIFE loses flow-narrowing
                const totalDays = sub.trial_started_at && sub.trial_ends_at
                  ? Math.round((new Date(sub.trial_ends_at).getTime() - new Date(sub.trial_started_at).getTime()) / 86400000)
                  : 14;
                const daysPassed = Math.max(0, totalDays - daysLeft);
                const pct = Math.min(100, Math.round((daysPassed / totalDays) * 100));
                const planObj = plans.find(p => p.tier === sub.plan_tier);
                const afterPrice = planObj && planObj.monthly_price_usd
                  ? `$${parseFloat(planObj.monthly_price_usd).toLocaleString()}`
                  : '₹1,999';
                const dayCards = [
                  { day: 3,         label: 'Guidance' },
                  { day: 7,         label: 'Highlights' },
                  { day: totalDays, label: 'Trial ends' },
                ];
                /* Arc geometry — matches reference SVG (radius 46, stroke 7) */
                const circumference = 2 * Math.PI * 46;
                const doneSegment = circumference / totalDays;            /* day 1 done */
                const remainingSegment = circumference * (trialDaysRemaining! / totalDays);
                return (
                  <>
                    <div className={styles.trialCard}>
                      {/* Eyebrow with teal pulsing dot */}
                      <span className={styles.trialEyebrow}>
                        Free trial · {(TIER_DISPLAY[sub.plan_tier] ?? sub.plan_tier)} plan
                      </span>

                      {/* Arc row: circular meter on LEFT, title/dates/prices on RIGHT */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginBottom: 4, position: 'relative', zIndex: 2 }}>
                        <div className={styles.trialCircleWrap}>
                          <svg viewBox="0 0 110 110" className={styles.trialCircleSvg}>
                            {/* Track */}
                            <circle cx="55" cy="55" r="46" fill="none"
                                    stroke="rgba(255,255,255,0.06)" strokeWidth="7" />
                            {/* Indigo — days already elapsed */}
                            <circle cx="55" cy="55" r="46" fill="none"
                                    stroke="var(--primary-600, #444ce7)" strokeWidth="7" strokeLinecap="round"
                                    strokeDasharray={`${doneSegment * Math.max(daysPassed, 1)} ${circumference}`} />
                            {/* Lighter indigo (solid, no gradient) — days remaining */}
                            <circle cx="55" cy="55" r="46" fill="none"
                                    stroke="var(--primary-400, #8098f9)" strokeWidth="7" strokeLinecap="round"
                                    strokeDasharray={`${remainingSegment} ${circumference}`}
                                    strokeDashoffset={-doneSegment * Math.max(daysPassed, 1)} />
                          </svg>
                          <div className={styles.trialCircleText}>
                            <div className={styles.trialCircleDays}>{daysLeft}</div>
                            <div className={styles.trialCircleLabel}>days left</div>
                          </div>
                        </div>
                        <div style={{ flex: 1, minWidth: 0, position: 'relative', zIndex: 2 }}>
                          <h3 className={styles.trialTitle}>Trial in<br />progress</h3>
                          <div className={styles.trialDates}>
                            {sub.trial_started_at && `Started: ${new Date(sub.trial_started_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}`}
                            <br />
                            {sub.trial_ends_at && `Ends: ${new Date(sub.trial_ends_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}`}
                          </div>
                          <div className={styles.trialPrices}>
                            <span className={styles.trialPriceNow}>₹0 now</span>
                            <span className={styles.trialPriceArrow} aria-hidden="true">→</span>
                            <span className={styles.trialPriceAfter}>
                              <strong style={{ color: 'rgba(255,255,255,0.52)', fontWeight: 600 }}>{afterPrice}/mo</strong> after
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Progress bar */}
                      <div className={styles.trialProgressBar}>
                        <div className={styles.trialProgressFill} style={{ width: `${pct}%` }} />
                      </div>
                      <div className={styles.trialProgressLabel}>
                        <span>Day {daysPassed} of {totalDays}</span>
                        <strong>{pct}% elapsed</strong>
                      </div>

                      {/* Milestone tiles */}
                      <div className={styles.trialDayGrid}>
                        {dayCards.map((dc) => {
                          const done = daysPassed > dc.day;
                          const cur = daysPassed >= dc.day - 2 && daysPassed <= dc.day;
                          const isEnd = dc.day === totalDays;
                          return (
                            <div
                              key={dc.day}
                              className={
                                `${styles.trialDayCard} ` +
                                `${done ? styles.trialDayCardDone : ''} ` +
                                `${!done && cur ? styles.trialDayCardCur : ''} ` +
                                `${!done && isEnd ? styles.trialDayCardEnd : ''}`
                              }
                            >
                              <div className={styles.trialDayCardLabel}>Day {dc.day}</div>
                              <div className={styles.trialDayCardSub}>{dc.label}</div>
                            </div>
                          );
                        })}
                      </div>

                      <div className={styles.trialButtonRow}>
                        <button
                          className={styles.trialUpgradeBtn}
                          onClick={() => navigate('/pricing')}
                        >
                          Upgrade Plan
                        </button>
                        <button
                          className={styles.trialSecondaryBtn}
                          onClick={() => navigate('/pricing')}
                        >
                          All Plans
                        </button>
                      </div>
                    </div>

                    {/* Trial timeline */}
                    <div className={styles.timelineCard}>
                      <div className={styles.timelineLabel}>TRIAL TIMELINE</div>
                      {(() => {
                        const events = [
                          { day: 1,         title: 'Trial activated',          desc: 'Full access enabled. Explore all features.' },
                          { day: 3,         title: 'Getting started guidance', desc: 'Tips to get more value early in your trial.' },
                          { day: 7,         title: 'Feature highlights',       desc: 'A closer look at the capabilities in your plan.' },
                          { day: 10,        title: 'Plan reminder',            desc: 'Review your options before access ends.' },
                          { day: totalDays, title: 'Trial access ends',        desc: 'Choose a plan to continue with full access.' },
                        ];
                        return events.map((ev) => {
                          const done    = daysPassed > ev.day;
                          const today   = daysPassed === ev.day || (daysPassed < ev.day && ev.day - daysPassed <= 0);
                          const status  = done ? 'done' : today ? 'today' : 'upcoming';
                          return (
                            <div key={ev.day} className={`${styles.timelineItem} ${styles[`timelineItem_${status}`] || ''}`}>
                              <div className={styles.timelineDot} aria-hidden="true" />
                              <div className={styles.timelineBody}>
                                <div className={styles.timelineDayLine}>
                                  <span className={styles.timelineDay}>Day {ev.day}</span>
                                  <span className={styles.timelineStatus}>
                                    — {status === 'done' ? 'Done' : status === 'today' ? 'Today' : 'Upcoming'}
                                  </span>
                                </div>
                                <div className={styles.timelineTitle}>{ev.title}</div>
                                <div className={styles.timelineDesc}>{ev.desc}</div>
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </>
                );
              })() : sub.status === 'trialing' && trialDaysRemaining === null ? (
                <div className={styles.trialCard}>
                  <h3 className={styles.trialTitle}>Trial ended</h3>
                  <p className={styles.trialDates}>Subscribe to regain full access to all features.</p>
                  <button className={styles.trialUpgradeBtn} onClick={() => navigate('/pricing')}>
                    Choose a Plan
                  </button>
                </div>
              ) : (
                <div className={styles.trialCard}>
                  <h3 className={styles.trialTitle}>Subscription active</h3>
                  <p className={styles.trialDates}>
                    {sub.current_period_end && `${sub.cancel_at_period_end ? 'Ends' : sub.auto_renews ? 'Renews automatically on' : 'Expires'} ${new Date(sub.current_period_end).toLocaleDateString()}`}
                  </p>
                </div>
              )}
            </div>
          </div>
          <ContractsCard />
        </section>
      )}


      

      {/* Payment method */}
      {sub?.payment_gateway && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Payment Method</h2>
          <div className={styles.paymentMethod}>
            {sub.payment_gateway === 'paypal' ? (
              <><span><Wallet size={18} aria-hidden /></span> <span>PayPal</span></>
            ) : (
              <><span><CreditCard size={18} aria-hidden /></span> <span>Credit / Debit Card</span></>
            )}
          </div>
        </section>
      )}

      {/* Contact Team Modal */}
      {contactOpen && (
        <div className={styles.dialogOverlay} onClick={() => setContactOpen(false)}>
          <div className={styles.dialog} onClick={e => e.stopPropagation()} style={{ maxWidth: 460, width: '100%' }}>
            <h3 style={{ margin: '0 0 6px 0', fontSize: 18, fontWeight: 800 }}>Contact Our Team</h3>
            <p style={{ margin: '0 0 18px 0', color: '#667085', fontSize: 14 }}>Send us a message and we'll get back to you shortly.</p>
            {contactSent ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ marginBottom: 10 }}><CheckCircle2 size={40} aria-hidden /></div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>Message sent!</div>
                <div style={{ color: '#667085', fontSize: 13, marginTop: 4 }}>We'll respond within 1–2 business days.</div>
                <button className={styles.upgradeBtn} style={{ marginTop: 18 }} onClick={() => setContactOpen(false)}>Close</button>
              </div>
            ) : (
              <form onSubmit={async (e) => {
                e.preventDefault();
                setContactSending(true); setContactError(null);
                try {
                  await sendContactMessage({ subject: contactSubject, message: contactMessage });
                  setContactSent(true); setContactSubject(''); setContactMessage('');
                } catch {
                  setContactError('Failed to send. Please try again.');
                } finally { setContactSending(false); }
              }}>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, color: 'var(--ds-text-secondary)' }}>Subject</label>
                  <input
                    type="text" required value={contactSubject}
                    onChange={e => setContactSubject(e.target.value)}
                    placeholder="e.g. Issue with my plan"
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid #eaecf0', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, color: 'var(--ds-text-secondary)' }}>Message</label>
                  <textarea
                    required value={contactMessage}
                    onChange={e => setContactMessage(e.target.value)}
                    placeholder="Describe your issue or question..."
                    rows={5}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid #eaecf0', fontSize: 14, outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
                  />
                </div>
                {contactError && <div style={{ color: '#d92d20', fontSize: 13, marginBottom: 10 }}>{contactError}</div>}
                <div className={styles.dialogActions}>
                  <button type="button" className={styles.dialogCancel} onClick={() => setContactOpen(false)}>Cancel</button>
                  <button type="submit" className={styles.upgradeBtn} disabled={contactSending}>
                    {contactSending ? 'Sending…' : 'Send Message'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Cancel confirm dialog */}
      {cancelConfirm && (
        <div className={styles.dialogOverlay}>
          <div className={styles.dialog}>
            <h3>Cancel subscription?</h3>
            <p>You'll keep access until the end of your current billing period.</p>
            <div className={styles.dialogActions}>
              <button
                className={styles.dialogCancel}
                onClick={handleCancel}
                disabled={cancelling}
              >
                {cancelling ? 'Cancelling…' : 'Yes, cancel'}
              </button>
              <button
                className={styles.dialogKeep}
                onClick={() => setCancelConfirm(false)}
              >
                Keep subscription
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingPage;
